import UserFormLogin from '@/models/user/forms/UserFormLogin'
import {
  createStore,
  MutationTree,
  ActionContext,
  ActionTree,
  GetterTree,
  Store as VuexStore,
  CommitOptions,
  DispatchOptions,
  createLogger
} from 'vuex'
import { Locale } from 'vue-i18n'
import UserState from '@/models/user/UserState'
import AuthServices from '@/services/AuthServices'

// declare state
export type State = {
  locale: Locale,
  user: UserState,
  accessToken: string,
  isLowFlowMode: boolean
};

// set state
const state: State = {
  locale: localStorage.getItem('locale') ?? 'fr',
  user: new UserState(),
  accessToken: localStorage.getItem('accessToken') ?? '',
  isLowFlowMode: localStorage.getItem('isLowFlowMode') !== null ? localStorage.getItem('isLowFlowMode') === 'true' : true
}

// mutations and action enums
export enum MutationTypes {
  SET_LOCALE = 'SET_LOCALE',
  SET_USER = 'SET_USER',
  SET_ACCESS_TOKEN = 'SET_ACCESS_TOKEN',
  SET_LOW_FLOW_MODE = 'SET_LOW_FLOW_MODE'
}

export enum ActionTypes {
  UPDATE_LOCALE = 'UPDATE_LOCALE',
  UPDATE_LOW_FLOW_MODE = 'UPDATE_LOW_FLOW_MODE',
  UPDATE_USER = 'UPDATE_USER',
  LOGIN_USER = 'LOGIN_USER',
  LOGOUT_USER = 'LOGOUT_USER'
}

// Mutation Types
export type Mutations<S = State> = {
  [MutationTypes.SET_LOCALE](state: S, payload: Locale): void;
  [MutationTypes.SET_USER](state: S, payload: UserState): void;
  [MutationTypes.SET_ACCESS_TOKEN](state: S, payload: string): void;
  [MutationTypes.SET_LOW_FLOW_MODE](state: S, payload: boolean): void;
};

// define mutations
const mutations: MutationTree<State> & Mutations = {
  [MutationTypes.SET_LOCALE] (state: State, payload: Locale) {
    state.locale = payload

    // Store language in the local storage (better solution than store it in a cookie)
    localStorage.setItem('locale', payload)
  },
  [MutationTypes.SET_USER] (state: State, payload: UserState) {
    state.user = payload
    localStorage.setItem('user', JSON.stringify(payload))
  },
  [MutationTypes.SET_ACCESS_TOKEN] (state: State, payload: string) {
    state.accessToken = payload
    localStorage.setItem('accessToken', payload)
  },
  [MutationTypes.SET_LOW_FLOW_MODE] (state: State, payload: boolean) {
    state.isLowFlowMode = payload

    // Register value in the local storage
    if (payload) {
      localStorage.setItem('isLowFlowMode', JSON.stringify(payload))
    } else {
      localStorage.removeItem('isLowFlowMode')
    }
  }
}

// actions
type AugmentedActionContext = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, State>, 'commit'>;

// actions interface
export interface Actions {
  [ActionTypes.UPDATE_LOCALE](
    { commit }: AugmentedActionContext,
    payload: Locale
  ): void;
  [ActionTypes.UPDATE_LOW_FLOW_MODE](
    { commit }: AugmentedActionContext,
    payload: boolean
  ): void;
  [ActionTypes.UPDATE_USER](
    { commit }: AugmentedActionContext,
    payload: UserState
  ): void;
  [ActionTypes.LOGIN_USER](
    { commit }: AugmentedActionContext,
    payload: UserFormLogin
  ): void;
  [ActionTypes.LOGOUT_USER](
    { commit }: AugmentedActionContext
  ): void;
}

export const actions: ActionTree<State, State> & Actions = {
  [ActionTypes.UPDATE_LOCALE] ({ commit }, payload: Locale) {
    commit(MutationTypes.SET_LOCALE, payload)
  },
  [ActionTypes.UPDATE_LOW_FLOW_MODE] ({ commit }, payload: boolean) {
    commit(MutationTypes.SET_LOW_FLOW_MODE, payload)
  },
  [ActionTypes.UPDATE_LOCALE] ({ commit }, payload: Locale) {
    commit(MutationTypes.SET_LOCALE, payload)
  },
  [ActionTypes.UPDATE_USER] ({ commit }, payload: UserState) {
    commit(MutationTypes.SET_USER, payload)
  },
  async [ActionTypes.LOGIN_USER] ({ commit }, payload: UserFormLogin) {
    await AuthServices.login(payload).then(response => {
      if (response.status === 200) {
        commit(MutationTypes.SET_USER, new UserState(
          response.data.user.id,
          response.data.user.email
        ))
        commit(MutationTypes.SET_ACCESS_TOKEN, response.data.accessToken)
      } else {
        console.log('Login error.')
      }
    })
  },
  [ActionTypes.LOGOUT_USER] ({ commit }) {
    commit(MutationTypes.SET_USER, new UserState())
    commit(MutationTypes.SET_ACCESS_TOKEN, '')
  }
}

// Getters types
export type Getters = {
  getLocale(state: State) : Locale,
  getUser(state: State) : UserState,
  getAccessToken(state: State) : string,
  isLoggedIn(state: State) : boolean,
  isLowFlowMode(state: State) : boolean
};

// getters
export const getters: GetterTree<State, State> & Getters = {
  getLocale: (state) => {
    return state.locale
  },
  getUser: (state) => {
    return state.user
  },
  getAccessToken: (state) => {
    return state.accessToken ?? ''
  },
  isLoggedIn: (state) => {
    return !!state.accessToken
  },
  isLowFlowMode: (state) => {
    return !!state.isLowFlowMode
  }
}

// setup store type
export type Store = Omit<
  VuexStore<State>,
  'commit' | 'getters' | 'dispatch'
> & {
  commit<K extends keyof Mutations, P extends Parameters<Mutations[K]>[1]>(
    key: K,
    payload: P,
    options?: CommitOptions
  ): ReturnType<Mutations[K]>;
} & {
  getters: {
    [K in keyof Getters]: ReturnType<Getters[K]>;
  };
} & {
  dispatch<K extends keyof Actions>(
    key: K,
    payload: Parameters<Actions[K]>[1],
    options?: DispatchOptions
  ): ReturnType<Actions[K]>;
};

export const store = createStore({
  state,
  mutations,
  actions,
  getters,
  plugins: [createLogger()]
})

export function useStore () : Store {
  return store as Store
}
